import { DamageElement, EffectId, SkillId, TileType, WEIGHT_SCALE } from "./constants.js";
/**
 * Catálogo de itens, **compartilhado** entre servidor e cliente.
 *
 * Mora no pacote de protocolo pelo mesmo motivo que os opcodes: é contrato. O cliente
 * precisa do nome, do peso e do desenho para montar a janela de inventário, e o servidor
 * precisa do peso e dos atributos para decidir o que cabe na mochila e quanto a espada
 * bate. Um catálogo por lado divergiria no primeiro item novo — e o sintoma seria uma
 * espada que pesa diferente na tela e no servidor.
 *
 * O que **não** está aqui é a tabela de loot: quem larga o quê é decisão do servidor, e
 * mandá-la para o cliente entregaria de graça a informação que o jogador deveria
 * descobrir jogando.
 */
/** Slots de equipamento. O índice é a posição no pacote de inventário. */
export const ItemSlot = {
    Head: 0,
    Amulet: 1,
    Backpack: 2,
    Armor: 3,
    Weapon: 4,
    Shield: 5,
    Legs: 6,
    Feet: 7,
    Ring: 8,
    Ammo: 9,
};
export const EQUIPMENT_SLOTS = 10;
export const SLOT_NAMES = {
    [ItemSlot.Head]: 'Cabeça',
    [ItemSlot.Amulet]: 'Amuleto',
    [ItemSlot.Backpack]: 'Mochila',
    [ItemSlot.Armor]: 'Corpo',
    [ItemSlot.Weapon]: 'Mão direita',
    [ItemSlot.Shield]: 'Mão esquerda',
    [ItemSlot.Legs]: 'Pernas',
    [ItemSlot.Feet]: 'Pés',
    [ItemSlot.Ring]: 'Anel',
    [ItemSlot.Ammo]: 'Munição',
};
export const ItemKind = {
    Generic: 0,
    Weapon: 1,
    Armor: 2,
    Shield: 3,
    Container: 4,
    Consumable: 5,
    Currency: 6,
    Ammo: 7,
    /**
     * Corpo de um jogador morto (PRD §6).
     *
     * É uma categoria própria e não um `Container` comum porque a diferença que importa é
     * exatamente a que o nome sugere: um contêiner se carrega, um corpo não. Distinguir
     * pela categoria deixa a recusa dizer a verdade — "isso não se carrega" — em vez de
     * fingir um limite de peso que ninguém teria como alcançar.
     */
    Corpse: 8,
    /**
     * Runa: magia guardada num objeto (PRD §6).
     *
     * O que a distingue de um consumível é para onde o efeito aponta. Uma poção age em quem
     * a bebe e por isso `UseItem` basta; uma runa age **em outra coisa**, e o gesto precisa
     * carregar um alvo — daí o opcode próprio (`UseOn`).
     */
    Rune: 9,
    /**
     * Ferramenta: a chave que o mapa reconhece (PRD §15.4).
     *
     * Pá, picareta, machado, faca e vara. São cinco objetos e **uma máquina só** — cada um
     * declara sobre que tile age e no que ele vira —, e é isso que resolve o problema mais
     * caro de um mundo aberto: controlar a ordem em que o jogador vê as coisas sem pôr um NPC
     * dizendo "volte no nível 20". A parede é o inventário dele, e ele **vê** a parede.
     *
     * E dá função ao peso: carregar as cinco custa capacidade, e escolher quais levar é a
     * decisão que o sistema de peso existe para provocar.
     *
     * Eram seis. A sexta era a corda, e ela não abria caminho: ela era o **pedágio de sair** do
     * fundo da caverna, que é a única coisa que uma ferramenta não deve ser — quem descia sem
     * ela descobria isso lá embaixo. O fundo ganhou escada como todo andar, e a corda saiu.
     */
    Tool: 10,
    /**
     * Chave: um item, com `keyId` de identidade, e não um item por porta.
     *
     * Três mil portas seriam três mil linhas no catálogo. A porta guarda o `keyId` que a abre
     * no `param` da própria feature, e a chave guarda o mesmo número — o encontro é de dois
     * inteiros, e o catálogo cresce com o número de **fechaduras diferentes**, não com o de
     * portas.
     */
    Key: 11,
    /**
     * Comida: o item que enche o relógio de saciedade (PRD §15.7).
     *
     * Categoria própria e não `Consumable`, apesar de os dois se gastarem ao serem usados,
     * porque o que a distingue é para **onde** o efeito vai e quando ele é recusado. Uma
     * poção age no instante e é recusada com a barra cheia; a comida age ao longo de vinte
     * minutos e é recusada com o **relógio** cheio, que é outro contador e outra frase.
     *
     * Misturá-las custaria caro num lugar específico: `consume` decide a recusa olhando
     * `healsHealth` e `healsMana`, e uma comida sem nenhum dos dois cairia no ramo de "isso
     * não teria efeito agora" — a comida seria recusada sempre.
     */
    Food: 12,
};
/**
 * Raridade de uma peça (PRD §15.5).
 *
 * **Visível**, e é o ponto: o cliente desenha a cor da borda do ícone a partir daqui. Sem
 * ela, um item raro é indistinguível de um comum até alguém comparar os números — e o
 * momento em que um loot raro cai é justamente o momento em que o jogo tem uma chance de
 * dizer "isto é diferente" sem escrever uma linha de texto.
 */
export const ItemRarity = {
    Common: 0,
    Uncommon: 1,
    Rare: 2,
    Epic: 3,
    /**
     * Lendário: o topo do que a caçada entrega.
     *
     * O degrau existe porque `Epic` estava fazendo dois trabalhos que não são o mesmo. Uma
     * peça de faixa 7 que cai de um Draconato a cada seis mortes e uma peça de faixa 8 que cai
     * de um Dragão a cada cinquenta são coisas de natureza diferente — a primeira é o que o
     * jogador **espera** da caçada de topo, a segunda é o que ele conta para os outros. Com
     * uma cor só para as duas, a segunda chega na tela sem nada dizendo que é a segunda.
     */
    Legendary: 4,
    /**
     * Celestial: a peça que quase ninguém vê (PRD §15.11).
     *
     * `CELESTIAL_DROP_CHANCE` é a chance dela, e é **cem-milionésima parte** de uma morte.
     * Não é um degrau a mais na escada de poder: é uma classe cuja função é existir no mundo
     * de outra gente. O jogador que a tem não a comprou de ninguém e não a encontrou porque
     * caçou o lugar certo — e é isso que ela diz sobre quem a carrega.
     *
     * O preço dessa raridade é que a peça precisa **valer a espera sem quebrar o teto**: as
     * seis peças do Alvorecer estão um degrau acima da faixa 8, e não três, porque um item
     * inacessível que também vence o jogo transformaria o azar de nunca vê-lo em desvantagem
     * permanente contra quem o viu.
     */
    Celestial: 5,
};
/** Cor da borda por raridade. Usada pelo cliente; declarada aqui porque é contrato. */
export const RARITY_COLORS = {
    [ItemRarity.Common]: '#3a4551',
    [ItemRarity.Uncommon]: '#4a8f5e',
    [ItemRarity.Rare]: '#3e7fc0',
    [ItemRarity.Epic]: '#a05fd0',
    // Âmbar e turquesa: as duas cores que faltavam no círculo cromático já ocupado por
    // cinza, verde, azul e roxo. Um segundo roxo mais claro seria o mais fácil de escrever e
    // o pior de ler — a borda lendária tem que se separar da épica **na mochila**, com 32 px
    // de lado e o ícone por cima, e não lado a lado numa paleta.
    [ItemRarity.Legendary]: '#e08a2c',
    [ItemRarity.Celestial]: '#7ff0e0',
};
/**
 * A chance de uma peça celestial sair de uma morte, em porcentagem (PRD §15.11).
 *
 * Um centésimo de milionésimo: uma em cem milhões. É um número que o sorteio de loot não
 * sabia representar — ele rolava `range(1, 100)` e toda chance abaixo de 1% era, na prática,
 * arredondada para "nunca ou 1%". `LOOT_ROLL_SCALE` é o que passou a caber.
 *
 * Constante, e não um literal repetido em cada linha de loot, porque é um **dial**: se a
 * classe celestial se provar rara demais para valer o código que a sustenta, o conserto é
 * este número e mais nada.
 */
export const CELESTIAL_DROP_CHANCE = 0.000001;
/**
 * Raridade máxima que um balcão de NPC pode ter na vitrine (PRD §15.11).
 *
 * A fronteira entre o que a cidade fornece e o que só o mundo entrega. Até `Rare`, o
 * jogador tem a opção de **comprar em vez de caçar** — o que é o que dá função ao ouro e o
 * que impede um azar de tabela de loot de travar a progressão de alguém. De `Epic` para
 * cima não há balcão, e a única forma de obter a peça é matar quem a larga ou negociar com
 * quem a matou.
 *
 * A regra é sobre a **vitrine**, e é a metade que faltava: `fallbackSellPrice` já recusava
 * comprar o que é raro demais, mas nada impedia alguém de escrever a Coroa do dragão ancião
 * na lista do Armeiro e apagar a razão de existir do canal de Comércio numa linha só.
 */
export const MAX_SHOP_RARITY = ItemRarity.Rare;
/**
 * Esta peça pode estar numa vitrine de NPC? Ver `MAX_SHOP_RARITY`.
 *
 * Raridade ausente conta como `Common`: a maior parte do catálogo não declara raridade
 * nenhuma — pá, pão, flecha —, e recusá-las por omissão esvaziaria as sete lojas.
 */
export function isShopStock(rarity) {
    return (rarity ?? ItemRarity.Common) <= MAX_SHOP_RARITY;
}
/**
 * O item guarda outros itens dentro?
 *
 * Contêiner e corpo, e a pergunta existe como função porque os dois lugares que precisam
 * dela — a criação da instância e a inserção — estão em módulos diferentes. Uma comparação
 * escrita à mão nos dois esqueceria o corpo no primeiro deles, e o sintoma seria um corpo
 * que aceita item e o perde.
 */
export function holdsItems(kind) {
    return kind === ItemKind.Container || kind === ItemKind.Corpse;
}
/** Máximo de unidades numa pilha. */
export const MAX_STACK = 100;
const oz = (value) => Math.round(value * WEIGHT_SCALE);
export const ITEMS = {
    1: {
        id: 1,
        name: 'Moeda de ouro',
        kind: ItemKind.Currency,
        weight: oz(0.1),
        stackable: true,
        slot: null,
        value: 1,
    },
    /**
     * Moeda de platina: uma pilha cheia de ouro num objeto que pesa como uma moeda (PRD §6).
     *
     * Existe por causa de uma conta que o jogador faz sozinho por volta da primeira caçada
     * séria: mil moedas de ouro são cem oz e **dez** slots de mochila, e a mochila tem vinte.
     * Sem um degrau acima, o teto de dinheiro carregável não é a capacidade do personagem, é a
     * quantidade de espaço que sobra depois do dinheiro — e a decisão que isso provoca não é
     * interessante, é só voltar à cidade.
     *
     * O peso é o mesmo do ouro **de propósito**: é o número que faz a troca valer a pena. Um
     * peso proporcional ao valor deixaria a platina bonita e inútil, e o item viraria um botão
     * que ninguém aperta.
     *
     * O `value` é `MAX_STACK`, e não é coincidência: **uma pilha cheia do degrau de baixo é uma
     * moeda do de cima**. É a única razão que o jogador confere de relance — a pilha lotou,
     * virou uma —, a que garante que juntar jamais precise de mais espaço do que libera, e a
     * que mantém o troco pequeno: quebrar uma platina devolve no máximo noventa e nove moedas,
     * que cabem no slot que a platina desocupou.
     *
     * A relação com o ouro não está escrita aqui nem em constante nenhuma: ela é a razão entre
     * os dois `value`, e é `COINS` que a lê. Um degrau novo é uma entrada neste catálogo e mais
     * nada — ver a moeda de cristal, logo abaixo, que não custou uma linha de lógica.
     */
    2: {
        id: 2,
        name: 'Moeda de platina',
        kind: ItemKind.Currency,
        weight: oz(0.1),
        stackable: true,
        slot: null,
        description: 'Vale cem de ouro e pesa como uma. É a pilha que virou moeda.',
        value: 100,
    },
    /**
     * Moeda de cristal: dez mil de ouro, e o teto prático do dinheiro carregado (PRD §6).
     *
     * O degrau que a platina sozinha não alcança. Com dois degraus, o dinheiro que cabe numa
     * mochila de vinte slots era duas mil moedas de platina — duzentas mil de ouro, o suficiente
     * para o problema voltar exatamente onde tinha sido resolvido, só que mais tarde. Com três,
     * a mochila comporta uma fortuna que nenhum personagem junta por acidente, e o assunto
     * "onde eu guardo isso" sai do caminho de vez.
     *
     * Cem platinas viram uma, pela mesma razão de sempre: pilha cheia embaixo, moeda em cima. É
     * por isso que o cristal é alcançável e uma moeda de dez mil de ouro *direta* não seria —
     * dez mil moedas soltas são cem pilhas, e nenhum contêiner do jogo tem cem slots. Juntar
     * dinheiro é sempre um degrau de cada vez, e a escada precisa ter todos.
     *
     * O peso continua o mesmo do ouro. Uma pilha de cristal é um milhão de moedas e dez oz —
     * absurdo de propósito: o dinheiro deixou de ser um problema de carga faz dois degraus, e
     * fingir o contrário aqui só reintroduziria a viagem à cidade que a platina aposentou.
     */
    3: {
        id: 3,
        name: 'Moeda de cristal',
        kind: ItemKind.Currency,
        weight: oz(0.1),
        stackable: true,
        slot: null,
        description: 'Vale dez mil de ouro. É o dinheiro que se carrega longe.',
        value: 10_000,
    },
    // --- Contêineres ---
    10: {
        id: 10,
        name: 'Mochila',
        kind: ItemKind.Container,
        weight: oz(18),
        stackable: false,
        slot: ItemSlot.Backpack,
        capacity: 20,
        value: 10,
    },
    11: {
        id: 11,
        name: 'Bolsa',
        kind: ItemKind.Container,
        weight: oz(4),
        stackable: false,
        slot: ItemSlot.Backpack,
        capacity: 8,
        value: 4,
    },
    // --- Armas corpo a corpo ---
    20: {
        id: 20,
        name: 'Espada curta',
        kind: ItemKind.Weapon,
        weight: oz(35),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 11,
        skill: SkillId.Sword,
        value: 30,
    },
    21: {
        id: 21,
        name: 'Machado de mão',
        kind: ItemKind.Weapon,
        weight: oz(40),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 13,
        skill: SkillId.Axe,
        value: 40,
    },
    22: {
        id: 22,
        name: 'Clava',
        kind: ItemKind.Weapon,
        weight: oz(30),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 10,
        skill: SkillId.Club,
        value: 15,
    },
    23: {
        id: 23,
        name: 'Espada longa',
        kind: ItemKind.Weapon,
        weight: oz(55),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 18,
        skill: SkillId.Sword,
        minLevel: 10,
        value: 200,
    },
    /**
     * Os bastões: a arma dos conjuradores, um par e não uma peça só.
     *
     * Existem porque Druid e Sorcerer nasciam com uma **clava** na mochila — uma arma que
     * §9.3 não atribui a nenhum dos dois (PRD §11.3, item 7). Sem alternativa, a escolha era
     * entre dar-lhes a arma errada e deixá-los de punho fechado, e as duas contradizem a
     * vocação.
     *
     * A skill é `Magic`, e não uma skill de arma: quem bate com um bastão está canalizando,
     * então o golpe escala com a mesma skill que a magia — e cada golpe conta como uma
     * tentativa contra as 1.600 que a magia exige, o que é ruído perto de treinar
     * conjurando, como deve ser.
     *
     * **São dois**, e essa é a parte que não é cosmética. Um bastão só significava que a
     * única diferença mecânica entre as duas vocações que conjuram era a lista de magias — o
     * gesto que o jogador repete o dia inteiro, o golpe básico, era idêntico. Com fogo de um
     * lado e gelo do outro, a escolha de vocação já aparece no primeiro rato: as resistências
     * do catálogo não são as mesmas para os dois elementos, e o Elemental de fogo é imune
     * justamente ao que o Sorcerer canaliza.
     *
     * O alcance de 3 é metade do arco (6) e não o iguala de propósito. O bastão não gasta
     * munição nenhuma — quem atira de graça não pode também atirar mais longe, senão o
     * Paladin vira a vocação que paga por flecha para fazer o que o Sorcerer faz melhor. Três
     * tiles é o suficiente para dar dois passos de recuo antes de o Rato encostar, que é o
     * que o alcance existe para comprar.
     */
    24: {
        id: 24,
        name: 'Bastão de fogo',
        kind: ItemKind.Weapon,
        weight: oz(28),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 9,
        skill: SkillId.Magic,
        range: 3,
        element: DamageElement.Fire,
        shot: { projectile: EffectId.Fireball, impact: EffectId.Fire },
        description: 'A gema está quente o bastante para incomodar a mão.',
        value: 25,
    },
    25: {
        id: 25,
        name: 'Bastão de gelo',
        kind: ItemKind.Weapon,
        weight: oz(28),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 9,
        skill: SkillId.Magic,
        range: 3,
        element: DamageElement.Ice,
        shot: { projectile: EffectId.Iceball, impact: EffectId.Ice },
        description: 'A gema não derrete, e a chuva que cai nela também não.',
        value: 25,
    },
    // --- Armas de distância ---
    30: {
        id: 30,
        name: 'Arco',
        kind: ItemKind.Weapon,
        weight: oz(30),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 12,
        skill: SkillId.Distance,
        range: 6,
        value: 100,
    },
    31: {
        id: 31,
        name: 'Flecha',
        kind: ItemKind.Ammo,
        weight: oz(0.7),
        stackable: true,
        slot: ItemSlot.Ammo,
        attack: 3,
        value: 1,
    },
    // --- Proteção ---
    40: {
        id: 40,
        name: 'Armadura de couro',
        kind: ItemKind.Armor,
        weight: oz(60),
        stackable: false,
        slot: ItemSlot.Armor,
        defense: 4,
        value: 50,
    },
    41: {
        id: 41,
        name: 'Cota de malha',
        kind: ItemKind.Armor,
        weight: oz(120),
        stackable: false,
        slot: ItemSlot.Armor,
        defense: 9,
        minLevel: 10,
        value: 350,
    },
    42: {
        id: 42,
        name: 'Elmo de couro',
        kind: ItemKind.Armor,
        weight: oz(20),
        stackable: false,
        slot: ItemSlot.Head,
        defense: 2,
        value: 20,
    },
    43: {
        id: 43,
        name: 'Calças de couro',
        kind: ItemKind.Armor,
        weight: oz(30),
        stackable: false,
        slot: ItemSlot.Legs,
        defense: 2,
        value: 25,
    },
    44: {
        id: 44,
        name: 'Botas de couro',
        kind: ItemKind.Armor,
        weight: oz(20),
        stackable: false,
        slot: ItemSlot.Feet,
        defense: 1,
        value: 15,
    },
    45: {
        id: 45,
        name: 'Escudo de madeira',
        kind: ItemKind.Shield,
        weight: oz(45),
        stackable: false,
        slot: ItemSlot.Shield,
        defense: 6,
        skill: SkillId.Shield,
        value: 40,
    },
    // --- Consumíveis ---
    50: {
        id: 50,
        name: 'Poção de vida',
        kind: ItemKind.Consumable,
        weight: oz(3),
        stackable: true,
        slot: null,
        healsHealth: 60,
        value: 45,
    },
    51: {
        id: 51,
        name: 'Poção de mana',
        kind: ItemKind.Consumable,
        weight: oz(3),
        stackable: true,
        slot: null,
        healsMana: 80,
        value: 50,
    },
    // --- Runas (PRD §6) ---
    /**
     * Runa em branco: a matéria-prima da conjuração em item.
     *
     * Existe para que conjurar uma runa custe **duas** coisas — mana e um objeto comprado —
     * e não só mana. Com o custo apenas em mana, um Druid parado num canto viraria uma
     * fábrica de cura infinita limitada por tempo de espera, que é o tipo de recurso que
     * ninguém decide gastar. Um insumo com preço em ouro liga a magia à economia.
     */
    80: {
        id: 80,
        name: 'Runa em branco',
        kind: ItemKind.Generic,
        weight: oz(4),
        stackable: true,
        slot: null,
        value: 10,
    },
    81: {
        id: 81,
        name: 'Runa de ataque',
        kind: ItemKind.Rune,
        weight: oz(4),
        stackable: true,
        slot: null,
        spellId: 50,
        minMagicLevel: 4,
        value: 30,
    },
    82: {
        id: 82,
        name: 'Runa de cura',
        kind: ItemKind.Rune,
        weight: oz(4),
        stackable: true,
        slot: null,
        spellId: 51,
        minMagicLevel: 3,
        value: 40,
    },
    83: {
        id: 83,
        name: 'Runa de explosão',
        kind: ItemKind.Rune,
        weight: oz(4),
        stackable: true,
        slot: null,
        spellId: 52,
        minMagicLevel: 6,
        value: 60,
    },
    /**
     * O corpo que fica onde um jogador morreu, com tudo que ele carregava dentro (PRD §6).
     *
     * A alternativa era o que existia antes: despejar cada item solto no tile. Funciona, e é
     * ilegível — um personagem morre com vinte pilhas na mochila e o chão vira uma coluna de
     * vinte ícones sobrepostos no mesmo quadrado de 32 px, sem nada dizendo que aquilo foi
     * uma morte e não um descarte. O corpo é **um** ícone que se reconhece de longe, marca o
     * lugar exato de onde voltar, e só se abre quando alguém chega e o revista.
     *
     * A capacidade é maior que a da mochila de propósito: ele precisa caber o inventário
     * inteiro de quem morreu, incluindo o conteúdo de bolsas dentro de bolsas, e um corpo que
     * "não coube" perderia item — que é exatamente o que ele existe para evitar.
     *
     * Peso alto **e** categoria própria: a categoria é o que recusa carregá-lo com uma
     * mensagem honesta, e o peso é a segunda linha de defesa para o caso de alguém acrescentar
     * um caminho novo de "pegar item" e esquecer da primeira.
     */
    70: {
        id: 70,
        name: 'Corpo',
        kind: ItemKind.Corpse,
        weight: oz(2000),
        stackable: false,
        slot: null,
        capacity: 64,
        value: 0,
    },
    /**
     * O corpo que já passou do ponto: o aviso de que ele vai sumir (PRD §15.7).
     *
     * Um segundo tipo de item, e não um sinalizador na instância, porque o que precisa mudar
     * é o **desenho** — e o que o cliente recebe do chão é `typeId` e quantidade, não o
     * estado interno da pilha. Um campo novo no `GroundItems` custaria um byte por pilha por
     * envio para dizer, na esmagadora maioria das vezes, "este corpo está fresco".
     *
     * Sem essa dica, todo desaparecimento vira acusação de roubo no chat: o jogador não tem
     * como distinguir "acabou o tempo" de "alguém pegou". Com ela, o corpo apodrecido é a
     * frase "corra" dita sem texto.
     *
     * Guarda o mesmo conteúdo e se revista igual — é o mesmo corpo, com outra cara.
     */
    71: {
        id: 71,
        name: 'Corpo apodrecido',
        kind: ItemKind.Corpse,
        weight: oz(2000),
        stackable: false,
        slot: null,
        capacity: 64,
        description: 'O que sobrou, e não sobra por muito tempo.',
        value: 0,
    },
    // --- Troféus, sem uso além de valor ---
    60: {
        id: 60,
        name: 'Pele de lobo',
        kind: ItemKind.Generic,
        weight: oz(9),
        stackable: true,
        slot: null,
        value: 15,
    },
    61: {
        id: 61,
        name: 'Presa de troll',
        kind: ItemKind.Generic,
        weight: oz(5),
        stackable: true,
        slot: null,
        value: 25,
    },
    62: {
        id: 62,
        name: 'Rabo de rato',
        kind: ItemKind.Generic,
        weight: oz(1),
        stackable: true,
        slot: null,
        value: 2,
    },
    // --- Ferramentas: cinco objetos, uma máquina só (PRD §15.4) ---
    //
    // Cada uma declara sobre que tile age e no que ele vira; `systems/tools.ts` é um laço
    // sobre esta tabela e não um `switch` de cinco ramos. Acrescentar a sexta é uma linha.
    //
    // `Sand` e `Rock` já existiam como tipo de tile e já estão espalhados pelo mundo — 309
    // tiles de areia e 204 de rocha nas duas regiões —, então metade do trabalho de mapa para
    // a pá e a picareta já estava feita antes de a ferramenta existir.
    //
    // O id **90 está vago**: era a corda, e ela saiu com o sistema de corda inteiro. O número
    // não é reaproveitado — id de item que muda de significado troca em silêncio o que está
    // guardado na mochila de quem já jogou.
    91: {
        id: 91,
        name: 'Pá',
        kind: ItemKind.Tool,
        weight: oz(38),
        stackable: false,
        slot: null,
        description: 'Uma pá de cabo curto. A areia cede a ela; a pedra, não.',
        tool: {
            on: [TileType.Sand],
            becomes: TileType.Hole,
            // Fecha sozinho, e é o que separa gesto tático de mudança de geografia: sem isso o
            // mapa vira queijo suíço permanente e a colisão desenhada deixa de valer.
            revertsAfterMs: 60_000,
            refusal: 'Você não pode cavar aqui.',
        },
        value: 50,
    },
    92: {
        id: 92,
        name: 'Picareta',
        kind: ItemKind.Tool,
        weight: oz(75),
        stackable: false,
        slot: null,
        description: 'Pesada, e a única coisa que abre caminho na rocha.',
        tool: {
            on: [TileType.Rock],
            becomes: TileType.CaveFloor,
            // Não volta. É a diferença entre um gesto tático e uma mudança de geografia — e o
            // reinício da região é o único que a desfaz, por §15.2.
            refusal: 'Não há rocha para quebrar aqui.',
        },
        value: 250,
    },
    93: {
        id: 93,
        name: 'Machado de lenhador',
        kind: ItemKind.Tool,
        weight: oz(60),
        stackable: false,
        slot: null,
        description: 'Derruba árvore e moita. Não é arma — o fio é para madeira.',
        tool: {
            on: [TileType.Tree, TileType.Bush],
            becomes: TileType.Floor,
            refusal: 'Não há o que cortar aqui.',
        },
        value: 120,
    },
    94: {
        id: 94,
        name: 'Faca',
        kind: ItemKind.Tool,
        weight: oz(8),
        stackable: false,
        slot: null,
        description: 'Pequena e afiada. Serve para retalhar o que se caçou.',
        tool: {
            on: [],
            refusal: 'Não há nada aqui para retalhar.',
        },
        value: 30,
    },
    95: {
        id: 95,
        name: 'Vara de pescar',
        kind: ItemKind.Tool,
        weight: oz(30),
        stackable: false,
        slot: null,
        description: 'Come isca e devolve peixe, quando devolve.',
        tool: {
            on: [TileType.Water],
            // A água não muda: pescar não gasta o lago.
            refusal: 'Não há água aqui.',
            yields: { typeId: 96, chance: 45 },
            skill: SkillId.Fist,
        },
        value: 80,
    },
    96: {
        id: 96,
        name: 'Peixe',
        kind: ItemKind.Food,
        weight: oz(3),
        stackable: true,
        slot: null,
        feedsMs: 360_000,
        description: 'Cru, e ainda assim melhor que fome.',
        value: 4,
    },
    // --- Chaves ---
    //
    // Um item por **fechadura**, e não por porta: a porta guarda no `param` da feature o
    // `keyId` que a abre, e a chave guarda o mesmo número.
    97: {
        id: 97,
        name: 'Chave de bronze',
        kind: ItemKind.Key,
        weight: oz(2),
        stackable: false,
        slot: null,
        keyId: 1,
        description: 'Uma chave de bronze, gasta de tanto uso.',
        value: 100,
    },
    // --- Equipamento por faixa, com caminho de obtenção (PRD §15.5) ---
    //
    // Cada peça declara **como se chega até ela** em `source`, e o campo não é decorativo: um
    // equipamento sem caminho de obtenção é um item que existe no catálogo e não no jogo, e o
    // jogador que o vê na loja de outro descobre que o mundo tem um degrau que ninguém lhe
    // disse como subir. A suíte confere que toda peça acima da faixa inicial tem um.
    //
    // A raridade é **visível** pelo mesmo motivo: `rarity` vira cor de borda no ícone, e sem
    // ela um item raro é indistinguível de um comum até alguém comparar os números.
    100: {
        id: 100,
        name: 'Espada de aço',
        kind: ItemKind.Weapon,
        weight: oz(38),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 22,
        skill: SkillId.Sword,
        minLevel: 15,
        tier: 4,
        rarity: ItemRarity.Uncommon,
        durability: 5000,
        source: 'Loot do Lobo negro (15%); à venda na Casa do Armeiro.',
        description: 'Aço de verdade, e não ferro batido. Pesa como tal.',
        value: 400,
    },
    101: {
        id: 101,
        name: 'Cota de malha',
        kind: ItemKind.Armor,
        weight: oz(120),
        stackable: false,
        slot: ItemSlot.Armor,
        defense: 14,
        minLevel: 15,
        tier: 4,
        rarity: ItemRarity.Uncommon,
        durability: 5000,
        source: 'Loot do Esqueleto (12%); à venda na Casa do Armeiro.',
        value: 450,
    },
    102: {
        id: 102,
        name: 'Machado de guerra',
        kind: ItemKind.Weapon,
        weight: oz(62),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 31,
        skill: SkillId.Axe,
        minLevel: 25,
        tier: 6,
        rarity: ItemRarity.Rare,
        durability: 6000,
        source: 'Loot do Ciclope (18%).',
        value: 1400,
    },
    103: {
        id: 103,
        name: 'Placa de escamas',
        kind: ItemKind.Armor,
        weight: oz(190),
        stackable: false,
        slot: ItemSlot.Armor,
        defense: 26,
        minLevel: 30,
        tier: 7,
        rarity: ItemRarity.Rare,
        durability: 7000,
        source: 'Loot do Draconato (15%) e do Dragão (25%).',
        description: 'Escamas costuradas em couro. Ainda cheira a fumaça.',
        value: 3200,
    },
    104: {
        id: 104,
        name: 'Cajado rúnico',
        kind: ItemKind.Weapon,
        weight: oz(42),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 14,
        skill: SkillId.Magic,
        minLevel: 25,
        tier: 6,
        rarity: ItemRarity.Rare,
        durability: 5000,
        source: 'Loot do Lich (20%).',
        description: 'A madeira guarda calor mesmo depois de solta.',
        value: 2600,
    },
    // --- Amuleto e anel: os primeiros, e os primeiros com **carga por instância** ---
    //
    // A carga mora na **instância** e não no tipo, e é a primeira coisa do catálogo que
    // precisa disso: dois amuletos idênticos na mesma mochila têm cargas diferentes, e um
    // campo no tipo diria que os dois se gastaram juntos. É também por isso que eles não
    // empilham — pilha é um monte de coisas iguais, e duas cargas diferentes não são iguais.
    // --- Comida: o piso gratuito de sustento (PRD §15.7) ---
    //
    // A saciedade é o que transforma regeneração de **propriedade automática do personagem**
    // em **consumível com peso, preço e logística**. É paga com tempo e atenção em vez de
    // dinheiro: sem ela, ou tudo é instantâneo e errar não custa nada, ou tudo é poção e o
    // nível 8 não sobrevive.
    //
    // A coluna que decide o catálogo não é a saciedade nem o peso, é a razão entre os dois —
    // **minutos por oz**. É ela que responde à única pergunta que o jogador faz na frente do
    // balcão: o que levar, já que a mochila também tem que caber poção e loot.
    //
    //   | item     | saciedade | peso   | min/oz | de onde vem              |
    //   |----------|-----------|--------|--------|--------------------------|
    //   | Cogumelo |      60 s | 0,2 oz |    5,0 | loot de caverna          |
    //   | Pão      |     120 s |   1 oz |    2,0 | padaria, 2 gp            |
    //   | Queijo   |     180 s | 1,5 oz |    2,0 | padaria, 3 gp            |
    //   | Carne    |     300 s |   4 oz |   1,25 | loot de canídeo e besta  |
    //   | Peixe    |     360 s |   3 oz |    2,0 | pesca                    |
    //   | Presunto |     600 s |   5 oz |    2,0 | açougue, 15 gp           |
    //
    // O Cogumelo é o mais eficiente em peso e o pior em volume — encher os vinte minutos
    // exige vinte deles, e vinte pilhas não cabem numa mochila que também leva poção. A Carne
    // é a mais cara em peso e não se compra: ela é o que cai da caça, e a decisão dela é
    // "comer aqui ou carregar para vender".
    //
    // **O Peixe fica no 96**, longe desta família, e não é descuido: ele já existia como
    // consumível, a vara de pescar aponta para o número em `tool.yields`, e — o que decide —
    // ele já pode estar gravado em `character_items` de alguém. Id de item é identidade
    // persistida; renumerar um item é trocar o que está na mochila de quem parou de jogar.
    120: {
        id: 120,
        name: 'Cogumelo',
        kind: ItemKind.Food,
        weight: oz(0.2),
        stackable: true,
        slot: null,
        feedsMs: 60_000,
        description: 'Cresce no escuro. Alimenta pouco e não pesa nada.',
        value: 3,
    },
    121: {
        id: 121,
        name: 'Pão',
        kind: ItemKind.Food,
        weight: oz(1),
        stackable: true,
        slot: null,
        feedsMs: 120_000,
        description: 'Assado hoje, provavelmente.',
        value: 2,
    },
    122: {
        id: 122,
        name: 'Queijo',
        kind: ItemKind.Food,
        weight: oz(1.5),
        stackable: true,
        slot: null,
        feedsMs: 180_000,
        description: 'Curado o bastante para viajar.',
        value: 3,
    },
    123: {
        id: 123,
        name: 'Carne',
        kind: ItemKind.Food,
        weight: oz(4),
        stackable: true,
        slot: null,
        feedsMs: 300_000,
        description: 'Pesada de carregar, e é o preço de não ter que comprá-la.',
        value: 6,
    },
    124: {
        id: 124,
        name: 'Presunto',
        kind: ItemKind.Food,
        weight: oz(5),
        stackable: true,
        slot: null,
        feedsMs: 600_000,
        description: 'Meia hora de caça num pedaço só.',
        value: 15,
    },
    110: {
        id: 110,
        name: 'Amuleto de vida',
        kind: ItemKind.Armor,
        weight: oz(6),
        stackable: false,
        slot: ItemSlot.Amulet,
        defense: 2,
        charges: 20,
        tier: 4,
        rarity: ItemRarity.Uncommon,
        source: 'Loot do Carniçal (10%).',
        description: 'Absorve o golpe que mataria, e se gasta ao fazê-lo.',
        value: 900,
    },
    111: {
        id: 111,
        name: 'Anel de força',
        kind: ItemKind.Armor,
        weight: oz(3),
        stackable: false,
        slot: ItemSlot.Ring,
        attack: 4,
        charges: 60,
        tier: 5,
        rarity: ItemRarity.Uncommon,
        source: 'Loot do Ogro (12%).',
        description: 'Aperta o dedo enquanto tem carga. Depois, só aperta.',
        value: 1100,
    },
    // ===========================================================================
    // O catálogo por raridade (PRD §15.11)
    // ===========================================================================
    //
    // O que havia antes eram vinte e quatro peças de equipamento para oito faixas de nível, e a
    // conta não fecha: uma faixa inteira do jogo — cinco níveis, algumas horas — se atravessava
    // sem uma única peça nova entrar na mochila. O sintoma não é "pouco item", é **progressão
    // muda**: subir de nível não mudava nada do que o jogador veste, e o loot de um Draconato
    // era ouro e mais ouro.
    //
    // ## A escada, e o que decide de que degrau cada peça é
    //
    // A raridade **não** é um segundo eixo de poder. Ela responde a uma pergunta só — *de onde
    // esta peça vem* — e o poder segue disso, não o contrário:
    //
    //   | raridade  | de onde vem                          | quem a tem              |
    //   |-----------|--------------------------------------|-------------------------|
    //   | Comum     | balcão, por moedas                   | qualquer um             |
    //   | Incomum   | balcão, ou loot frequente            | quem caçou uma tarde    |
    //   | Raro      | balcão caro, ou loot de faixa alta   | quem juntou ouro        |
    //   | Épico     | **só** loot de faixa 6–7, ou troca   | quem caça o topo        |
    //   | Lendário  | **só** loot de faixa 8, ou troca     | quem caça o topo muito  |
    //   | Celestial | uma em cem milhões, ou troca         | quase ninguém           |
    //
    // A fronteira que importa é entre `Rare` e `Epic`, e ela está em `MAX_SHOP_RARITY`: até o
    // raro, o ouro é caminho; do épico para cima, não há caminho que não passe por matar ou por
    // negociar com quem matou. É essa parede que dá o que fazer ao canal de Comércio e à mesa de
    // troca de §13.6 — sem ela, todo item tem preço de tabela e ninguém precisa conversar.
    //
    // ## Por que as peças vêm em conjuntos, e por que os conjuntos têm nome
    //
    // Cada faixa alta tem um **conjunto** — placas na 5, guerra na 6, crepúsculo na 7, dragão
    // ancião na 8 — e não seis peças avulsas de nomes diferentes. É o mesmo argumento da
    // paleta por material de `art/items.ts`: o jogador precisa ler "isto é da mesma faixa que a
    // minha bota" sem comparar dois números de defesa. O nome faz metade do trabalho que a
    // borda colorida faz, e faz para quem joga com o som ligado e a janela pequena.
    //
    // ## O que **não** está aqui
    //
    // Nenhuma peça nova tem propriedade que o motor não saiba executar. Não há resistência
    // elemental em armadura, não há bônus de skill, não há efeito ao acertar: são todos campos
    // que exigiriam sistema novo, e um catálogo que promete o que o servidor não cumpre é pior
    // que um catálogo pequeno. O que estas peças têm é ataque, defesa, peso, nível e carga —
    // que é exatamente o que `applyEquipment` lê.
    // --- Espadas: da adaga que se compra no primeiro dia à lâmina que mata dragão ---
    200: {
        id: 200,
        name: 'Adaga',
        kind: ItemKind.Weapon,
        weight: oz(12),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 7,
        skill: SkillId.Sword,
        tier: 1,
        rarity: ItemRarity.Common,
        description: 'Curta demais para um duelo, barata o bastante para o primeiro rato.',
        value: 15,
    },
    201: {
        id: 201,
        name: 'Sabre',
        kind: ItemKind.Weapon,
        weight: oz(32),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 14,
        skill: SkillId.Sword,
        minLevel: 5,
        tier: 2,
        rarity: ItemRarity.Common,
        value: 90,
    },
    202: {
        id: 202,
        name: 'Espada bastarda',
        kind: ItemKind.Weapon,
        weight: oz(48),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 26,
        skill: SkillId.Sword,
        minLevel: 20,
        tier: 5,
        rarity: ItemRarity.Rare,
        durability: 5500,
        source: 'Loot do Ogro (14%); à venda na Casa do Armeiro.',
        description: 'Uma mão e meia: pega-se com as duas quando o braço cansa.',
        value: 950,
    },
    203: {
        id: 203,
        name: 'Espada de duas mãos',
        kind: ItemKind.Weapon,
        weight: oz(78),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 34,
        skill: SkillId.Sword,
        minLevel: 28,
        tier: 6,
        rarity: ItemRarity.Rare,
        durability: 6000,
        source: 'Loot do Ciclope (12%); à venda na Casa do Armeiro.',
        value: 2000,
    },
    204: {
        id: 204,
        name: 'Lâmina do ocaso',
        kind: ItemKind.Weapon,
        weight: oz(52),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 41,
        skill: SkillId.Sword,
        minLevel: 32,
        tier: 7,
        rarity: ItemRarity.Epic,
        durability: 7000,
        source: 'Loot do Espectro (10%). Nenhum balcão a vende.',
        description: 'A lâmina não reflete a tocha. Reflete o que está atrás dela.',
        value: 5200,
    },
    205: {
        id: 205,
        name: 'Espada matadragões',
        kind: ItemKind.Weapon,
        weight: oz(68),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 47,
        skill: SkillId.Sword,
        minLevel: 36,
        tier: 8,
        rarity: ItemRarity.Legendary,
        durability: 8000,
        source: 'Loot do Dragão (6%). Nenhum balcão a vende.',
        description: 'Forjada por quem perdeu o irmão para um. Serviu.',
        value: 12000,
    },
    // --- Machados ---
    210: {
        id: 210,
        name: 'Machadinha',
        kind: ItemKind.Weapon,
        weight: oz(24),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 9,
        skill: SkillId.Axe,
        tier: 1,
        rarity: ItemRarity.Common,
        value: 20,
    },
    211: {
        id: 211,
        name: 'Machado duplo',
        kind: ItemKind.Weapon,
        weight: oz(52),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 20,
        skill: SkillId.Axe,
        minLevel: 12,
        tier: 3,
        rarity: ItemRarity.Common,
        value: 260,
    },
    212: {
        id: 212,
        name: 'Alabarda',
        kind: ItemKind.Weapon,
        weight: oz(90),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 27,
        skill: SkillId.Axe,
        minLevel: 22,
        tier: 5,
        rarity: ItemRarity.Rare,
        durability: 5500,
        source: 'Loot do Ciclope (12%); à venda na Casa do Armeiro.',
        description: 'Pesa como um portão e alcança como um deles.',
        value: 1100,
    },
    213: {
        id: 213,
        name: 'Machado do carrasco',
        kind: ItemKind.Weapon,
        weight: oz(95),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 37,
        skill: SkillId.Axe,
        minLevel: 30,
        tier: 7,
        rarity: ItemRarity.Epic,
        durability: 7000,
        source: 'Loot do Draconato (10%). Nenhum balcão o vende.',
        value: 5000,
    },
    214: {
        id: 214,
        name: 'Machado do trovão',
        kind: ItemKind.Weapon,
        weight: oz(88),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 45,
        skill: SkillId.Axe,
        minLevel: 36,
        tier: 8,
        rarity: ItemRarity.Legendary,
        durability: 8000,
        source: 'Loot do Elemental de energia (6%). Nenhum balcão o vende.',
        description: 'Estala antes de acertar. Ninguém sabe explicar por quê.',
        value: 12500,
    },
    // --- Clavas ---
    220: {
        id: 220,
        name: 'Porrete ferrado',
        kind: ItemKind.Weapon,
        weight: oz(38),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 15,
        skill: SkillId.Club,
        minLevel: 6,
        tier: 2,
        rarity: ItemRarity.Common,
        value: 110,
    },
    221: {
        id: 221,
        name: 'Maça',
        kind: ItemKind.Weapon,
        weight: oz(55),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 22,
        skill: SkillId.Club,
        minLevel: 14,
        tier: 4,
        rarity: ItemRarity.Uncommon,
        durability: 5000,
        source: 'Loot do Esqueleto (10%); à venda na Casa do Armeiro.',
        value: 430,
    },
    222: {
        id: 222,
        name: 'Malho de guerra',
        kind: ItemKind.Weapon,
        weight: oz(85),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 30,
        skill: SkillId.Club,
        minLevel: 24,
        tier: 6,
        rarity: ItemRarity.Rare,
        durability: 6000,
        source: 'Loot do Ciclope (10%); à venda na Casa do Armeiro.',
        value: 1600,
    },
    223: {
        id: 223,
        name: 'Martelo do juízo',
        kind: ItemKind.Weapon,
        weight: oz(92),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 38,
        skill: SkillId.Club,
        minLevel: 32,
        tier: 7,
        rarity: ItemRarity.Epic,
        durability: 7000,
        source: 'Loot do Elemental de gelo (10%). Nenhum balcão o vende.',
        value: 5100,
    },
    224: {
        id: 224,
        name: 'Marreta do titã',
        kind: ItemKind.Weapon,
        weight: oz(120),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 46,
        skill: SkillId.Club,
        minLevel: 37,
        tier: 8,
        rarity: ItemRarity.Legendary,
        durability: 8000,
        source: 'Loot do Lich (5%). Nenhum balcão a vende.',
        description: 'Duas mãos, e ainda assim as duas reclamam.',
        value: 13000,
    },
    // --- Cajados: a linha de quem bate com a mesma perícia que conjura ---
    //
    // A escala de ataque é **metade** da das armas de músculo na mesma faixa, e é de propósito:
    // o golpe do cajado escala com `SkillId.Magic`, que é a mesma perícia da magia, e quem a
    // treina já tem o dano no botão. Igualar os números faria do conjurador o melhor espadachim
    // do jogo por acidente de catálogo.
    230: {
        id: 230,
        name: 'Varinha de aprendiz',
        kind: ItemKind.Weapon,
        weight: oz(12),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 7,
        skill: SkillId.Magic,
        tier: 1,
        rarity: ItemRarity.Common,
        value: 60,
    },
    231: {
        id: 231,
        name: 'Cajado de carvalho',
        kind: ItemKind.Weapon,
        weight: oz(30),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 11,
        skill: SkillId.Magic,
        minLevel: 8,
        tier: 2,
        rarity: ItemRarity.Common,
        value: 180,
    },
    232: {
        id: 232,
        name: 'Cajado de cristal',
        kind: ItemKind.Weapon,
        weight: oz(40),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 16,
        skill: SkillId.Magic,
        minLevel: 20,
        tier: 5,
        rarity: ItemRarity.Rare,
        durability: 5000,
        source: 'Loot da Aranha gigante (14%); à venda na Casa do Armeiro.',
        value: 1300,
    },
    233: {
        id: 233,
        name: 'Cajado do vazio',
        kind: ItemKind.Weapon,
        weight: oz(44),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 21,
        skill: SkillId.Magic,
        minLevel: 30,
        tier: 7,
        rarity: ItemRarity.Epic,
        durability: 6000,
        source: 'Loot do Espectro (12%). Nenhum balcão o vende.',
        description: 'A ponta não tem gema. Tem o buraco onde uma ficaria.',
        value: 5400,
    },
    234: {
        id: 234,
        name: 'Cajado das eras',
        kind: ItemKind.Weapon,
        weight: oz(46),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 25,
        skill: SkillId.Magic,
        minLevel: 37,
        tier: 8,
        rarity: ItemRarity.Legendary,
        durability: 8000,
        source: 'Loot do Lich (6%). Nenhum balcão o vende.',
        value: 13500,
    },
    // --- Distância, e a munição que cada uma come ---
    //
    // O motor não amarra munição a arma: `ItemSlot.Ammo` é um slot só e o ataque soma. A
    // separação entre flecha, virote e pedra é de **preço e peso**, que é a decisão que sobra
    // — e é uma decisão de verdade, porque quem leva mil pedras não leva mais nada.
    240: {
        id: 240,
        name: 'Funda',
        kind: ItemKind.Weapon,
        weight: oz(8),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 8,
        skill: SkillId.Distance,
        range: 4,
        tier: 1,
        rarity: ItemRarity.Common,
        description: 'Um couro e um nó. A munição está no chão, em toda parte.',
        value: 35,
    },
    241: {
        id: 241,
        name: 'Besta',
        kind: ItemKind.Weapon,
        weight: oz(60),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 19,
        skill: SkillId.Distance,
        range: 5,
        minLevel: 12,
        tier: 3,
        rarity: ItemRarity.Common,
        value: 430,
    },
    242: {
        id: 242,
        name: 'Arco composto',
        kind: ItemKind.Weapon,
        weight: oz(34),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 24,
        skill: SkillId.Distance,
        range: 7,
        minLevel: 20,
        tier: 5,
        rarity: ItemRarity.Rare,
        durability: 5000,
        source: 'Loot da Serpente (12%); à venda na Casa do Armeiro.',
        value: 1400,
    },
    243: {
        id: 243,
        name: 'Balestra pesada',
        kind: ItemKind.Weapon,
        weight: oz(85),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 31,
        skill: SkillId.Distance,
        range: 6,
        minLevel: 28,
        tier: 6,
        rarity: ItemRarity.Rare,
        durability: 6000,
        source: 'Loot do Draconato (12%); à venda na Casa do Armeiro.',
        value: 2400,
    },
    244: {
        id: 244,
        name: 'Arco do caçador eterno',
        kind: ItemKind.Weapon,
        weight: oz(40),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 39,
        skill: SkillId.Distance,
        range: 8,
        minLevel: 35,
        tier: 8,
        rarity: ItemRarity.Legendary,
        durability: 8000,
        source: 'Loot do Dragão (5%). Nenhum balcão o vende.',
        description: 'A corda está tensa desde antes de você nascer.',
        value: 13000,
    },
    245: {
        id: 245,
        name: 'Virote',
        kind: ItemKind.Ammo,
        weight: oz(0.9),
        stackable: true,
        slot: ItemSlot.Ammo,
        attack: 5,
        rarity: ItemRarity.Common,
        value: 2,
    },
    246: {
        id: 246,
        name: 'Flecha de fogo',
        kind: ItemKind.Ammo,
        weight: oz(0.7),
        stackable: true,
        slot: ItemSlot.Ammo,
        attack: 7,
        rarity: ItemRarity.Uncommon,
        description: 'A ponta vem embebida. Acende no atrito da corda.',
        value: 5,
    },
    247: {
        id: 247,
        name: 'Pedra de funda',
        kind: ItemKind.Ammo,
        weight: oz(0.5),
        stackable: true,
        slot: ItemSlot.Ammo,
        attack: 2,
        rarity: ItemRarity.Common,
        description: 'É uma pedra. Redonda, o que já é alguma coisa.',
        value: 1,
    },
    248: {
        id: 248,
        name: 'Flecha perfurante',
        kind: ItemKind.Ammo,
        weight: oz(0.8),
        stackable: true,
        slot: ItemSlot.Ammo,
        attack: 10,
        rarity: ItemRarity.Rare,
        source: 'Loot do Troll batedor (20%); à venda na Casa do Armeiro.',
        value: 15,
    },
    249: {
        id: 249,
        name: 'Virote sagrado',
        kind: ItemKind.Ammo,
        weight: oz(1),
        stackable: true,
        slot: ItemSlot.Ammo,
        attack: 12,
        rarity: ItemRarity.Epic,
        source: 'Loot do Espectro (15%). Nenhum balcão o vende.',
        description: 'Benzido um a um, e é por isso que não há muitos.',
        value: 40,
    },
    // --- Proteção de corpo ---
    //
    // Duas linhas paralelas e não uma: a de **placa**, que defende muito e pesa muito, e a de
    // **manto**, que defende cerca de metade pelo mesmo nível e pesa um quinto. É a única
    // diferenciação de vocação que o catálogo consegue fazer sem sistema novo — quem conjura
    // gasta a capacidade em runa e poção, e uma armadura de 210 oz é a decisão de não levá-las.
    250: {
        id: 250,
        name: 'Túnica acolchoada',
        kind: ItemKind.Armor,
        weight: oz(40),
        stackable: false,
        slot: ItemSlot.Armor,
        defense: 3,
        tier: 1,
        rarity: ItemRarity.Common,
        value: 30,
    },
    251: {
        id: 251,
        name: 'Gibão de couro batido',
        kind: ItemKind.Armor,
        weight: oz(70),
        stackable: false,
        slot: ItemSlot.Armor,
        defense: 6,
        minLevel: 8,
        tier: 2,
        rarity: ItemRarity.Common,
        value: 120,
    },
    252: {
        id: 252,
        name: 'Brigantina',
        kind: ItemKind.Armor,
        weight: oz(105),
        stackable: false,
        slot: ItemSlot.Armor,
        defense: 11,
        minLevel: 12,
        tier: 3,
        rarity: ItemRarity.Common,
        value: 260,
    },
    253: {
        id: 253,
        name: 'Peitoral de placas',
        kind: ItemKind.Armor,
        weight: oz(210),
        stackable: false,
        slot: ItemSlot.Armor,
        defense: 19,
        minLevel: 22,
        tier: 5,
        rarity: ItemRarity.Rare,
        durability: 6000,
        source: 'Loot do Ogro (12%); à venda na Casa do Armeiro.',
        value: 1500,
    },
    254: {
        id: 254,
        name: 'Armadura de guerra',
        kind: ItemKind.Armor,
        weight: oz(230),
        stackable: false,
        slot: ItemSlot.Armor,
        defense: 23,
        minLevel: 26,
        tier: 6,
        rarity: ItemRarity.Rare,
        durability: 6500,
        source: 'Loot do Elemental de fogo (12%); à venda na Casa do Armeiro.',
        value: 2600,
    },
    255: {
        id: 255,
        name: 'Couraça do crepúsculo',
        kind: ItemKind.Armor,
        weight: oz(200),
        stackable: false,
        slot: ItemSlot.Armor,
        defense: 29,
        minLevel: 32,
        tier: 7,
        rarity: ItemRarity.Epic,
        durability: 7000,
        source: 'Loot do Elemental de gelo (10%). Nenhum balcão a vende.',
        value: 6000,
    },
    256: {
        id: 256,
        name: 'Armadura do dragão ancião',
        kind: ItemKind.Armor,
        weight: oz(240),
        stackable: false,
        slot: ItemSlot.Armor,
        defense: 35,
        minLevel: 37,
        tier: 8,
        rarity: ItemRarity.Legendary,
        durability: 8000,
        source: 'Loot do Dragão (7%). Nenhum balcão a vende.',
        description: 'As escamas ainda estão quentes. Sempre estiveram.',
        value: 14000,
    },
    257: {
        id: 257,
        name: 'Manto do conjurador',
        kind: ItemKind.Armor,
        weight: oz(35),
        stackable: false,
        slot: ItemSlot.Armor,
        defense: 8,
        minLevel: 14,
        tier: 4,
        rarity: ItemRarity.Uncommon,
        source: 'Loot do Carniçal (12%); à venda na Botica.',
        description: 'Não para o machado. Para o vento, e para o peso na mochila.',
        value: 450,
    },
    258: {
        id: 258,
        name: 'Manto rúnico',
        kind: ItemKind.Armor,
        weight: oz(45),
        stackable: false,
        slot: ItemSlot.Armor,
        defense: 13,
        minLevel: 26,
        tier: 6,
        rarity: ItemRarity.Rare,
        durability: 5000,
        source: 'Loot do Elemental de fogo (12%); à venda na Botica.',
        value: 2100,
    },
    259: {
        id: 259,
        name: 'Manto das constelações',
        kind: ItemKind.Armor,
        weight: oz(40),
        stackable: false,
        slot: ItemSlot.Armor,
        defense: 18,
        minLevel: 34,
        tier: 7,
        rarity: ItemRarity.Epic,
        durability: 6000,
        source: 'Loot do Lich (12%). Nenhum balcão o vende.',
        description: 'O bordado muda de lugar quando você não está olhando.',
        value: 6500,
    },
    // --- Cabeça ---
    260: {
        id: 260,
        name: 'Capuz de pano',
        kind: ItemKind.Armor,
        weight: oz(8),
        stackable: false,
        slot: ItemSlot.Head,
        defense: 1,
        tier: 1,
        rarity: ItemRarity.Common,
        value: 8,
    },
    261: {
        id: 261,
        name: 'Elmo de aço',
        kind: ItemKind.Armor,
        weight: oz(45),
        stackable: false,
        slot: ItemSlot.Head,
        defense: 5,
        minLevel: 12,
        tier: 3,
        rarity: ItemRarity.Common,
        value: 180,
    },
    262: {
        id: 262,
        name: 'Elmo com viseira',
        kind: ItemKind.Armor,
        weight: oz(60),
        stackable: false,
        slot: ItemSlot.Head,
        defense: 8,
        minLevel: 20,
        tier: 5,
        rarity: ItemRarity.Rare,
        durability: 5000,
        source: 'Loot do Elemental de terra (14%); à venda na Casa do Armeiro.',
        value: 900,
    },
    263: {
        id: 263,
        name: 'Grande elmo',
        kind: ItemKind.Armor,
        weight: oz(80),
        stackable: false,
        slot: ItemSlot.Head,
        defense: 11,
        minLevel: 28,
        tier: 6,
        rarity: ItemRarity.Rare,
        durability: 6000,
        source: 'Loot do Elemental de fogo (10%); à venda na Casa do Armeiro.',
        value: 1800,
    },
    264: {
        id: 264,
        name: 'Elmo do crepúsculo',
        kind: ItemKind.Armor,
        weight: oz(65),
        stackable: false,
        slot: ItemSlot.Head,
        defense: 14,
        minLevel: 33,
        tier: 7,
        rarity: ItemRarity.Epic,
        durability: 7000,
        source: 'Loot do Draconato (10%). Nenhum balcão o vende.',
        value: 4200,
    },
    265: {
        id: 265,
        name: 'Coroa do dragão ancião',
        kind: ItemKind.Armor,
        weight: oz(70),
        stackable: false,
        slot: ItemSlot.Head,
        defense: 17,
        minLevel: 37,
        tier: 8,
        rarity: ItemRarity.Legendary,
        durability: 8000,
        source: 'Loot do Dragão (5%). Nenhum balcão a vende.',
        value: 9500,
    },
    266: {
        id: 266,
        name: 'Tiara de mago',
        kind: ItemKind.Armor,
        weight: oz(10),
        stackable: false,
        slot: ItemSlot.Head,
        defense: 3,
        minLevel: 14,
        tier: 4,
        rarity: ItemRarity.Uncommon,
        source: 'Loot do Carniçal (10%); à venda na Botica.',
        value: 380,
    },
    267: {
        id: 267,
        name: 'Diadema estelar',
        kind: ItemKind.Armor,
        weight: oz(12),
        stackable: false,
        slot: ItemSlot.Head,
        defense: 6,
        minLevel: 32,
        tier: 7,
        rarity: ItemRarity.Epic,
        source: 'Loot do Elemental de energia (12%). Nenhum balcão o vende.',
        value: 4500,
    },
    // --- Pernas ---
    270: {
        id: 270,
        name: 'Calças de pano',
        kind: ItemKind.Armor,
        weight: oz(12),
        stackable: false,
        slot: ItemSlot.Legs,
        defense: 1,
        tier: 1,
        rarity: ItemRarity.Common,
        value: 6,
    },
    271: {
        id: 271,
        name: 'Perneiras de malha',
        kind: ItemKind.Armor,
        weight: oz(55),
        stackable: false,
        slot: ItemSlot.Legs,
        defense: 4,
        minLevel: 12,
        tier: 3,
        rarity: ItemRarity.Common,
        value: 160,
    },
    272: {
        id: 272,
        name: 'Grevas de placas',
        kind: ItemKind.Armor,
        weight: oz(90),
        stackable: false,
        slot: ItemSlot.Legs,
        defense: 7,
        minLevel: 22,
        tier: 5,
        rarity: ItemRarity.Rare,
        durability: 5000,
        source: 'Loot do Elemental de terra (12%); à venda na Casa do Armeiro.',
        value: 850,
    },
    273: {
        id: 273,
        name: 'Perneiras do crepúsculo',
        kind: ItemKind.Armor,
        weight: oz(80),
        stackable: false,
        slot: ItemSlot.Legs,
        defense: 10,
        minLevel: 32,
        tier: 7,
        rarity: ItemRarity.Epic,
        durability: 7000,
        source: 'Loot do Elemental de gelo (10%). Nenhum balcão as vende.',
        value: 3800,
    },
    274: {
        id: 274,
        name: 'Grevas do dragão ancião',
        kind: ItemKind.Armor,
        weight: oz(95),
        stackable: false,
        slot: ItemSlot.Legs,
        defense: 13,
        minLevel: 37,
        tier: 8,
        rarity: ItemRarity.Legendary,
        durability: 8000,
        source: 'Loot do Dragão (5%). Nenhum balcão as vende.',
        value: 8800,
    },
    275: {
        id: 275,
        name: 'Saiote rúnico',
        kind: ItemKind.Armor,
        weight: oz(25),
        stackable: false,
        slot: ItemSlot.Legs,
        defense: 5,
        minLevel: 20,
        tier: 5,
        rarity: ItemRarity.Rare,
        source: 'Loot do Carniçal (12%); à venda na Botica.',
        value: 780,
    },
    // --- Pés ---
    280: {
        id: 280,
        name: 'Sapatos de pano',
        kind: ItemKind.Armor,
        weight: oz(8),
        stackable: false,
        slot: ItemSlot.Feet,
        defense: 1,
        tier: 1,
        rarity: ItemRarity.Common,
        value: 5,
    },
    281: {
        id: 281,
        name: 'Botas de viagem',
        kind: ItemKind.Armor,
        weight: oz(18),
        stackable: false,
        slot: ItemSlot.Feet,
        defense: 2,
        minLevel: 6,
        tier: 2,
        rarity: ItemRarity.Common,
        description: 'Solado grosso. Não corre mais rápido; dói menos no fim do dia.',
        value: 60,
    },
    282: {
        id: 282,
        name: 'Botas de placas',
        kind: ItemKind.Armor,
        weight: oz(55),
        stackable: false,
        slot: ItemSlot.Feet,
        defense: 5,
        minLevel: 22,
        tier: 5,
        rarity: ItemRarity.Rare,
        durability: 5000,
        source: 'Loot do Ogro (12%); à venda na Casa do Armeiro.',
        value: 700,
    },
    283: {
        id: 283,
        name: 'Botas do crepúsculo',
        kind: ItemKind.Armor,
        weight: oz(45),
        stackable: false,
        slot: ItemSlot.Feet,
        defense: 7,
        minLevel: 32,
        tier: 7,
        rarity: ItemRarity.Epic,
        durability: 7000,
        source: 'Loot do Espectro (10%). Nenhum balcão as vende.',
        value: 3200,
    },
    284: {
        id: 284,
        name: 'Botas do dragão ancião',
        kind: ItemKind.Armor,
        weight: oz(60),
        stackable: false,
        slot: ItemSlot.Feet,
        defense: 9,
        minLevel: 37,
        tier: 8,
        rarity: ItemRarity.Legendary,
        durability: 8000,
        source: 'Loot do Dragão (5%). Nenhum balcão as vende.',
        value: 7800,
    },
    285: {
        id: 285,
        name: 'Sandálias do conjurador',
        kind: ItemKind.Armor,
        weight: oz(12),
        stackable: false,
        slot: ItemSlot.Feet,
        defense: 3,
        minLevel: 18,
        tier: 4,
        rarity: ItemRarity.Uncommon,
        source: 'Loot da Aranha gigante (12%); à venda na Botica.',
        value: 400,
    },
    // --- Escudos ---
    290: {
        id: 290,
        name: 'Broquel',
        kind: ItemKind.Shield,
        weight: oz(25),
        stackable: false,
        slot: ItemSlot.Shield,
        defense: 4,
        skill: SkillId.Shield,
        tier: 1,
        rarity: ItemRarity.Common,
        value: 20,
    },
    291: {
        id: 291,
        name: 'Escudo de aço',
        kind: ItemKind.Shield,
        weight: oz(65),
        stackable: false,
        slot: ItemSlot.Shield,
        defense: 11,
        skill: SkillId.Shield,
        minLevel: 12,
        tier: 3,
        rarity: ItemRarity.Common,
        value: 280,
    },
    292: {
        id: 292,
        name: 'Escudo torre',
        kind: ItemKind.Shield,
        weight: oz(120),
        stackable: false,
        slot: ItemSlot.Shield,
        defense: 17,
        skill: SkillId.Shield,
        minLevel: 22,
        tier: 5,
        rarity: ItemRarity.Rare,
        durability: 5500,
        source: 'Loot do Ciclope (12%); à venda na Casa do Armeiro.',
        description: 'Do queixo ao joelho. Enxerga-se pouco atrás dele, e é o preço.',
        value: 1300,
    },
    293: {
        id: 293,
        name: 'Escudo do vigia',
        kind: ItemKind.Shield,
        weight: oz(100),
        stackable: false,
        slot: ItemSlot.Shield,
        defense: 21,
        skill: SkillId.Shield,
        minLevel: 28,
        tier: 6,
        rarity: ItemRarity.Rare,
        durability: 6000,
        source: 'Loot do Draconato (12%); à venda na Casa do Armeiro.',
        value: 2400,
    },
    294: {
        id: 294,
        name: 'Égide do crepúsculo',
        kind: ItemKind.Shield,
        weight: oz(110),
        stackable: false,
        slot: ItemSlot.Shield,
        defense: 26,
        skill: SkillId.Shield,
        minLevel: 33,
        tier: 7,
        rarity: ItemRarity.Epic,
        durability: 7000,
        source: 'Loot do Elemental de gelo (8%). Nenhum balcão a vende.',
        value: 5800,
    },
    295: {
        id: 295,
        name: 'Escudo do dragão ancião',
        kind: ItemKind.Shield,
        weight: oz(130),
        stackable: false,
        slot: ItemSlot.Shield,
        defense: 31,
        skill: SkillId.Shield,
        minLevel: 37,
        tier: 8,
        rarity: ItemRarity.Legendary,
        durability: 8000,
        source: 'Loot do Dragão (5%). Nenhum balcão o vende.',
        value: 13500,
    },
    // --- Amuletos e anéis: a família que se gasta por instância ---
    //
    // Todos com `charges`, e por isso nenhum empilha (ver `ItemType.charges`). É a família
    // que a Joalheira vende, e a razão de ela ser o maior sorvedouro de ouro da vila: a peça
    // com carga é **consumível de luxo**, e não relíquia de baú.
    300: {
        id: 300,
        name: 'Talismã de osso',
        kind: ItemKind.Armor,
        weight: oz(4),
        stackable: false,
        slot: ItemSlot.Amulet,
        defense: 1,
        charges: 10,
        tier: 3,
        rarity: ItemRarity.Common,
        value: 200,
    },
    301: {
        id: 301,
        name: 'Amuleto de vigor',
        kind: ItemKind.Armor,
        weight: oz(6),
        stackable: false,
        slot: ItemSlot.Amulet,
        defense: 3,
        charges: 25,
        tier: 5,
        rarity: ItemRarity.Rare,
        source: 'Loot da Serpente (10%); à venda na Ourivesaria.',
        value: 1400,
    },
    302: {
        id: 302,
        name: 'Colar do lobo',
        kind: ItemKind.Armor,
        weight: oz(5),
        stackable: false,
        slot: ItemSlot.Amulet,
        attack: 3,
        charges: 40,
        tier: 4,
        rarity: ItemRarity.Uncommon,
        source: 'Loot do Lobo negro (12%); à venda na Ourivesaria.',
        description: 'Dentes que já foram de quem caçava melhor que você.',
        value: 800,
    },
    303: {
        id: 303,
        name: 'Amuleto do ocaso',
        kind: ItemKind.Armor,
        weight: oz(6),
        stackable: false,
        slot: ItemSlot.Amulet,
        defense: 5,
        charges: 30,
        tier: 7,
        rarity: ItemRarity.Epic,
        source: 'Loot do Lich (12%). Nenhum balcão o vende.',
        value: 5500,
    },
    304: {
        id: 304,
        name: 'Gargantilha estelar',
        kind: ItemKind.Armor,
        weight: oz(7),
        stackable: false,
        slot: ItemSlot.Amulet,
        attack: 3,
        defense: 7,
        charges: 50,
        tier: 8,
        rarity: ItemRarity.Legendary,
        source: 'Loot do Elemental de energia (5%). Nenhum balcão a vende.',
        value: 15000,
    },
    310: {
        id: 310,
        name: 'Anel de cobre',
        kind: ItemKind.Armor,
        weight: oz(2),
        stackable: false,
        slot: ItemSlot.Ring,
        defense: 1,
        charges: 30,
        tier: 2,
        rarity: ItemRarity.Common,
        value: 120,
    },
    311: {
        id: 311,
        name: 'Anel de precisão',
        kind: ItemKind.Armor,
        weight: oz(3),
        stackable: false,
        slot: ItemSlot.Ring,
        attack: 3,
        charges: 50,
        tier: 4,
        rarity: ItemRarity.Uncommon,
        source: 'Loot da Vespa gigante (10%); à venda na Ourivesaria.',
        value: 900,
    },
    312: {
        id: 312,
        name: 'Anel do guardião',
        kind: ItemKind.Armor,
        weight: oz(3),
        stackable: false,
        slot: ItemSlot.Ring,
        defense: 4,
        charges: 60,
        tier: 6,
        rarity: ItemRarity.Rare,
        source: 'Loot do Espectro (12%); à venda na Ourivesaria.',
        value: 2000,
    },
    313: {
        id: 313,
        name: 'Anel do vazio',
        kind: ItemKind.Armor,
        weight: oz(3),
        stackable: false,
        slot: ItemSlot.Ring,
        attack: 7,
        charges: 70,
        tier: 7,
        rarity: ItemRarity.Epic,
        source: 'Loot do Elemental de energia (10%). Nenhum balcão o vende.',
        value: 6200,
    },
    314: {
        id: 314,
        name: 'Anel das eras',
        kind: ItemKind.Armor,
        weight: oz(4),
        stackable: false,
        slot: ItemSlot.Ring,
        attack: 9,
        defense: 4,
        charges: 90,
        tier: 8,
        rarity: ItemRarity.Legendary,
        source: 'Loot do Lich (5%). Nenhum balcão o vende.',
        value: 16000,
    },
    // --- Consumíveis: a escada de poção ---
    //
    // O par de §6 — 60 de vida, 80 de mana — é o degrau do meio de uma escada que não existia:
    // uma poção só é uma poção que ou desperdiça cura no nível 8 ou não segura o nível 35. Os
    // três degraus custam **mais que proporcionalmente** de propósito, porque o que se compra
    // na poção grande não é cura, é **espaço de mochila e tempo de balcão**.
    320: {
        id: 320,
        name: 'Poção de vida pequena',
        kind: ItemKind.Consumable,
        weight: oz(2),
        stackable: true,
        slot: null,
        healsHealth: 30,
        rarity: ItemRarity.Common,
        value: 22,
    },
    321: {
        id: 321,
        name: 'Poção de vida forte',
        kind: ItemKind.Consumable,
        weight: oz(4),
        stackable: true,
        slot: null,
        healsHealth: 140,
        rarity: ItemRarity.Uncommon,
        value: 130,
    },
    322: {
        id: 322,
        name: 'Poção de vida suprema',
        kind: ItemKind.Consumable,
        weight: oz(5),
        stackable: true,
        slot: null,
        healsHealth: 300,
        rarity: ItemRarity.Rare,
        value: 400,
    },
    323: {
        id: 323,
        name: 'Poção de mana pequena',
        kind: ItemKind.Consumable,
        weight: oz(2),
        stackable: true,
        slot: null,
        healsMana: 40,
        rarity: ItemRarity.Common,
        value: 25,
    },
    324: {
        id: 324,
        name: 'Poção de mana forte',
        kind: ItemKind.Consumable,
        weight: oz(4),
        stackable: true,
        slot: null,
        healsMana: 180,
        rarity: ItemRarity.Uncommon,
        value: 140,
    },
    325: {
        id: 325,
        name: 'Poção de mana suprema',
        kind: ItemKind.Consumable,
        weight: oz(5),
        stackable: true,
        slot: null,
        healsMana: 380,
        rarity: ItemRarity.Rare,
        value: 420,
    },
    326: {
        id: 326,
        name: 'Água benta',
        kind: ItemKind.Consumable,
        weight: oz(4),
        stackable: true,
        slot: null,
        healsHealth: 80,
        healsMana: 80,
        rarity: ItemRarity.Rare,
        description: 'Cura pouco dos dois lados, e é a única coisa que cura os dois.',
        value: 300,
    },
    327: {
        id: 327,
        name: 'Elixir do dragão',
        kind: ItemKind.Consumable,
        weight: oz(6),
        stackable: true,
        slot: null,
        healsHealth: 250,
        healsMana: 250,
        rarity: ItemRarity.Legendary,
        source: 'Loot do Dragão (8%). Nenhum balcão o vende.',
        description: 'Fervido do que o Dragão guardava na garganta. Não pergunte.',
        value: 3000,
    },
    // --- Runas ---
    //
    // As três novas apontam para magias que **já existem em forma**, mas com entradas próprias
    // no catálogo de magias (53 a 55): uma runa é uma magia sem vocação, sem palavra e sem
    // custo de mana, e reaproveitar a entrada da Bola de Fogo do Sorcerer daria à runa o
    // `minLevel` e a lista de vocações dela. Ver o bloco de runas em `spells.ts`.
    //
    // Elas **não** têm magia de conjuração correspondente, e é uma escolha: as três de §6 são o
    // ofício do Druid, e estas são mercadoria de balcão e de loot. Um segundo trio conjurável
    // duplicaria o laço sem acrescentar decisão nenhuma.
    330: {
        id: 330,
        name: 'Runa de veneno',
        kind: ItemKind.Rune,
        weight: oz(4),
        stackable: true,
        slot: null,
        spellId: 53,
        minMagicLevel: 5,
        rarity: ItemRarity.Uncommon,
        value: 55,
    },
    331: {
        id: 331,
        name: 'Runa de gelo',
        kind: ItemKind.Rune,
        weight: oz(4),
        stackable: true,
        slot: null,
        spellId: 54,
        minMagicLevel: 7,
        rarity: ItemRarity.Uncommon,
        description: 'Prende os pés de quem a leva. É para isso que se compra.',
        value: 80,
    },
    332: {
        id: 332,
        name: 'Runa de relâmpago',
        kind: ItemKind.Rune,
        weight: oz(4),
        stackable: true,
        slot: null,
        spellId: 55,
        minMagicLevel: 9,
        rarity: ItemRarity.Rare,
        value: 120,
    },
    // --- Comida: dez cozinhas para um relógio de vinte minutos ---
    //
    // A coluna que decide continua sendo **minutos por oz** (ver a tabela de §15.7). O que os
    // novos acrescentam é a ponta barata — maçã e cenoura enchem um minuto e não pesam nada,
    // que é o que cabe na mochila de quem está sem espaço — e a ponta cara, em que uma peça só
    // fecha o relógio inteiro.
    340: {
        id: 340,
        name: 'Maçã',
        kind: ItemKind.Food,
        weight: oz(0.4),
        stackable: true,
        slot: null,
        feedsMs: 45_000,
        rarity: ItemRarity.Common,
        value: 1,
    },
    341: {
        id: 341,
        name: 'Cenoura',
        kind: ItemKind.Food,
        weight: oz(0.4),
        stackable: true,
        slot: null,
        feedsMs: 60_000,
        rarity: ItemRarity.Common,
        value: 1,
    },
    342: {
        id: 342,
        name: 'Ovo cozido',
        kind: ItemKind.Food,
        weight: oz(0.8),
        stackable: true,
        slot: null,
        feedsMs: 90_000,
        rarity: ItemRarity.Common,
        value: 2,
    },
    343: {
        id: 343,
        name: 'Uvas',
        kind: ItemKind.Food,
        weight: oz(0.5),
        stackable: true,
        slot: null,
        feedsMs: 90_000,
        rarity: ItemRarity.Common,
        value: 2,
    },
    344: {
        id: 344,
        name: 'Pão de mel',
        kind: ItemKind.Food,
        weight: oz(1.2),
        stackable: true,
        slot: null,
        feedsMs: 200_000,
        rarity: ItemRarity.Common,
        value: 5,
    },
    345: {
        id: 345,
        name: 'Sopa quente',
        kind: ItemKind.Food,
        weight: oz(3),
        stackable: true,
        slot: null,
        feedsMs: 240_000,
        rarity: ItemRarity.Common,
        description: 'Enche mais o ânimo que o estômago, e ainda assim enche.',
        value: 8,
    },
    346: {
        id: 346,
        name: 'Peixe assado',
        kind: ItemKind.Food,
        weight: oz(3),
        stackable: true,
        slot: null,
        feedsMs: 540_000,
        rarity: ItemRarity.Common,
        description: 'O mesmo peixe da vara, meia hora de fogo depois.',
        value: 12,
    },
    347: {
        id: 347,
        name: 'Torta de carne',
        kind: ItemKind.Food,
        weight: oz(4.5),
        stackable: true,
        slot: null,
        feedsMs: 480_000,
        rarity: ItemRarity.Common,
        value: 20,
    },
    348: {
        id: 348,
        name: 'Costela assada',
        kind: ItemKind.Food,
        weight: oz(6),
        stackable: true,
        slot: null,
        feedsMs: 720_000,
        rarity: ItemRarity.Uncommon,
        value: 28,
    },
    349: {
        id: 349,
        name: 'Banquete do dragão',
        kind: ItemKind.Food,
        weight: oz(8),
        stackable: true,
        slot: null,
        // Fecha o relógio inteiro de uma vez (`FOOD_MAX_MS`). É o único item do catálogo que
        // faz isso, e é por isso que ele não se compra: um balcão que vendesse vinte minutos de
        // saciedade por moeda apagaria o sistema de §15.7 em vez de coroá-lo.
        feedsMs: 1_200_000,
        rarity: ItemRarity.Epic,
        source: 'Loot do Dragão (10%). Nenhum balcão o vende.',
        description: 'O que sobrou do que ele comeu. Dá para uma semana.',
        value: 900,
    },
    // --- Troféus e matéria-prima: o que sobra da caça ---
    //
    // Sem uso mecânico e com `value` alto, que é o desenho certo para eles: o troféu é o que
    // converte tempo de caça em ouro **sem** passar por equipamento, e é o que dá função ao
    // Curtidor. Todos ficam em `Common` ou `Uncommon` de propósito — uma raridade de `Rare`
    // para cima os tornaria invendáveis por `fallbackSellPrice`, e um troféu que nenhum balcão
    // compra é um troféu que não é troféu.
    350: {
        id: 350,
        name: 'Pele de urso',
        kind: ItemKind.Generic,
        weight: oz(14),
        stackable: true,
        slot: null,
        rarity: ItemRarity.Common,
        value: 30,
    },
    351: {
        id: 351,
        name: 'Dente de lobo',
        kind: ItemKind.Generic,
        weight: oz(1),
        stackable: true,
        slot: null,
        rarity: ItemRarity.Common,
        value: 8,
    },
    352: {
        id: 352,
        name: 'Casco de javali',
        kind: ItemKind.Generic,
        weight: oz(2),
        stackable: true,
        slot: null,
        rarity: ItemRarity.Common,
        value: 10,
    },
    353: {
        id: 353,
        name: 'Ferrão de escorpião',
        kind: ItemKind.Generic,
        weight: oz(2),
        stackable: true,
        slot: null,
        rarity: ItemRarity.Common,
        value: 35,
    },
    354: {
        id: 354,
        name: 'Seda de aranha',
        kind: ItemKind.Generic,
        weight: oz(1),
        stackable: true,
        slot: null,
        rarity: ItemRarity.Uncommon,
        description: 'Mais forte que linha de mesmo fio. Ninguém sabe tecê-la ainda.',
        value: 40,
    },
    355: {
        id: 355,
        name: 'Asa de morcego',
        kind: ItemKind.Generic,
        weight: oz(0.5),
        stackable: true,
        slot: null,
        rarity: ItemRarity.Common,
        value: 6,
    },
    356: {
        id: 356,
        name: 'Osso',
        kind: ItemKind.Generic,
        weight: oz(3),
        stackable: true,
        slot: null,
        rarity: ItemRarity.Common,
        value: 5,
    },
    357: {
        id: 357,
        name: 'Crânio',
        kind: ItemKind.Generic,
        weight: oz(6),
        stackable: true,
        slot: null,
        rarity: ItemRarity.Common,
        value: 45,
    },
    358: {
        id: 358,
        name: 'Essência de terra',
        kind: ItemKind.Generic,
        weight: oz(2),
        stackable: true,
        slot: null,
        rarity: ItemRarity.Uncommon,
        value: 120,
    },
    359: {
        id: 359,
        name: 'Essência de fogo',
        kind: ItemKind.Generic,
        weight: oz(2),
        stackable: true,
        slot: null,
        rarity: ItemRarity.Uncommon,
        value: 140,
    },
    360: {
        id: 360,
        name: 'Essência de gelo',
        kind: ItemKind.Generic,
        weight: oz(2),
        stackable: true,
        slot: null,
        rarity: ItemRarity.Uncommon,
        value: 145,
    },
    361: {
        id: 361,
        name: 'Essência de energia',
        kind: ItemKind.Generic,
        weight: oz(2),
        stackable: true,
        slot: null,
        rarity: ItemRarity.Uncommon,
        value: 160,
    },
    362: {
        id: 362,
        name: 'Escama de draconato',
        kind: ItemKind.Generic,
        weight: oz(3),
        stackable: true,
        slot: null,
        rarity: ItemRarity.Uncommon,
        value: 180,
    },
    363: {
        id: 363,
        name: 'Escama de dragão',
        kind: ItemKind.Generic,
        weight: oz(4),
        stackable: true,
        slot: null,
        rarity: ItemRarity.Uncommon,
        description: 'Uma só cobre a palma da mão. O bicho tem milhares.',
        value: 320,
    },
    364: {
        id: 364,
        name: 'Chifre de ciclope',
        kind: ItemKind.Generic,
        weight: oz(12),
        stackable: true,
        slot: null,
        rarity: ItemRarity.Uncommon,
        value: 260,
    },
    365: {
        id: 365,
        name: 'Coração de troll',
        kind: ItemKind.Generic,
        weight: oz(5),
        stackable: true,
        slot: null,
        rarity: ItemRarity.Common,
        value: 90,
    },
    366: {
        id: 366,
        name: 'Poeira espectral',
        kind: ItemKind.Generic,
        weight: oz(0.5),
        stackable: true,
        slot: null,
        rarity: ItemRarity.Uncommon,
        description: 'Pesa quase nada e não assenta. Fica pairando na palma.',
        value: 220,
    },
    // --- Contêineres e as duas ferramentas que faltavam ---
    410: {
        id: 410,
        name: 'Bolsa de caçador',
        kind: ItemKind.Container,
        weight: oz(8),
        stackable: false,
        slot: ItemSlot.Backpack,
        capacity: 12,
        rarity: ItemRarity.Common,
        value: 20,
    },
    411: {
        id: 411,
        name: 'Mochila reforçada',
        kind: ItemKind.Container,
        weight: oz(22),
        stackable: false,
        slot: ItemSlot.Backpack,
        capacity: 28,
        rarity: ItemRarity.Rare,
        description: 'Oito bolsos a mais, e a costura que aguenta o que eles pesam.',
        value: 900,
    },
    412: {
        id: 412,
        name: 'Aljava',
        kind: ItemKind.Container,
        weight: oz(6),
        stackable: false,
        slot: ItemSlot.Backpack,
        capacity: 10,
        rarity: ItemRarity.Common,
        value: 45,
    },
    413: {
        id: 413,
        name: 'Foice',
        kind: ItemKind.Tool,
        weight: oz(34),
        stackable: false,
        slot: null,
        description: 'Corta moita, e só moita. Contra árvore é gesto de quem não trouxe machado.',
        tool: {
            // Só a moita: é a ferramenta barata de quem precisa abrir passagem no mato e não vai
            // derrubar árvore nenhuma. Metade do preço do machado de lenhador, metade do trabalho.
            on: [TileType.Bush],
            becomes: TileType.Floor,
            refusal: 'A foice não pega nisso.',
        },
        value: 60,
    },
    414: {
        id: 414,
        name: 'Rede de pesca',
        kind: ItemKind.Tool,
        weight: oz(45),
        stackable: false,
        slot: null,
        description: 'Pega mais que a vara, e pesa como o que ela pega.',
        tool: {
            on: [TileType.Water],
            refusal: 'Não há água aqui.',
            // 70% contra os 45% da vara, por 15 oz a mais e três vezes o preço. É a mesma decisão
            // de sempre: o que cabe na mochila junto.
            yields: { typeId: 96, chance: 70 },
            skill: SkillId.Fist,
        },
        value: 220,
    },
    // ===========================================================================
    // As peças do Alvorecer: a classe celestial (PRD §15.11)
    // ===========================================================================
    //
    // Seis peças e um fragmento, todas com `CELESTIAL_DROP_CHANCE` — uma em cem milhões, das
    // quatro criaturas de faixa 8. Não há balcão, não há missão e não há receita: o único
    // caminho é a morte de um Dragão, de um Lich ou de um Elemental, e o segundo caminho é
    // convencer quem já teve o primeiro.
    //
    // O nome não é enfeite. O Alvorecer é a divindade que o Sacerdote Anselmo cita ao explicar
    // por que o templo devolve a vida (§15.8), e era a única lore escrita do mundo — a única
    // coisa que o jogo afirma sobre si mesmo além da geografia. Pendurar a classe mais rara
    // nela é o que faz o item mais improvável do catálogo **significar** alguma coisa em vez de
    // ser só o número maior: quem carrega a Lâmina do Alvorecer carrega uma frase que um NPC da
    // vila diz de graça para qualquer um que pergunte.
    //
    // Os números ficam **um degrau** acima da faixa 8, e não três. Um item que quase ninguém
    // pode ter e que também decide todo combate transformaria o azar de nunca vê-lo em
    // desvantagem permanente — e um jogo em que a diferença entre dois jogadores é um sorteio
    // de cem milhões é um jogo em que a caçada deixa de ser o que decide.
    900: {
        id: 900,
        name: 'Lâmina do Alvorecer',
        kind: ItemKind.Weapon,
        weight: oz(60),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 58,
        skill: SkillId.Sword,
        minLevel: 40,
        tier: 8,
        rarity: ItemRarity.Celestial,
        durability: 12000,
        source: 'Loot do Dragão (0,000001%). Não existe balcão que a venda.',
        description: 'A primeira manhã, presa numa lâmina. Não precisa de tocha.',
        value: 200_000,
    },
    901: {
        id: 901,
        name: 'Égide do Alvorecer',
        kind: ItemKind.Shield,
        weight: oz(115),
        stackable: false,
        slot: ItemSlot.Shield,
        defense: 38,
        skill: SkillId.Shield,
        minLevel: 40,
        tier: 8,
        rarity: ItemRarity.Celestial,
        durability: 12000,
        source: 'Loot do Dragão (0,000001%). Não existe balcão que a venda.',
        value: 200_000,
    },
    902: {
        id: 902,
        name: 'Coroa do Alvorecer',
        kind: ItemKind.Armor,
        weight: oz(55),
        stackable: false,
        slot: ItemSlot.Head,
        defense: 21,
        minLevel: 40,
        tier: 8,
        rarity: ItemRarity.Celestial,
        durability: 12000,
        source: 'Loot do Lich (0,000001%). Não existe balcão que a venda.',
        value: 180_000,
    },
    903: {
        id: 903,
        name: 'Manto do Alvorecer',
        kind: ItemKind.Armor,
        weight: oz(90),
        stackable: false,
        slot: ItemSlot.Armor,
        defense: 42,
        minLevel: 40,
        tier: 8,
        rarity: ItemRarity.Celestial,
        durability: 12000,
        source: 'Loot do Elemental de energia (0,000001%). Não existe balcão que o venda.',
        description: 'Leve como pano e não rasga. Ninguém conseguiu cortar um.',
        value: 220_000,
    },
    904: {
        id: 904,
        name: 'Anel do Alvorecer',
        kind: ItemKind.Armor,
        weight: oz(3),
        stackable: false,
        slot: ItemSlot.Ring,
        attack: 12,
        defense: 6,
        charges: 200,
        minLevel: 40,
        tier: 8,
        rarity: ItemRarity.Celestial,
        source: 'Loot do Lich (0,000001%). Não existe balcão que o venda.',
        value: 190_000,
    },
    905: {
        id: 905,
        name: 'Cajado do Alvorecer',
        kind: ItemKind.Weapon,
        weight: oz(42),
        stackable: false,
        slot: ItemSlot.Weapon,
        attack: 31,
        skill: SkillId.Magic,
        minLevel: 40,
        tier: 8,
        rarity: ItemRarity.Celestial,
        durability: 12000,
        source: 'Loot do Elemental de energia (0,000001%). Não existe balcão que o venda.',
        value: 200_000,
    },
    906: {
        id: 906,
        name: 'Fragmento do Alvorecer',
        kind: ItemKind.Generic,
        weight: oz(1),
        stackable: true,
        slot: null,
        rarity: ItemRarity.Celestial,
        // Cem vezes mais provável que as peças, e ainda assim uma em um milhão. Existe porque
        // uma classe inteira cuja única prova de existência é uma peça que ninguém viu é uma
        // classe indistinguível de um boato: o fragmento é a evidência barata de que a coisa é
        // real, e é a única celestial que dois jogadores têm chance de conversar sobre.
        source: 'Loot das criaturas de faixa 8 (0,0001%). Não existe balcão que o compre.',
        description: 'Um caco de luz parada. Continua morno depois de horas na mochila.',
        value: 25_000,
    },
};
export function itemType(typeId) {
    return ITEMS[typeId];
}
// --- Dinheiro (PRD §6) -------------------------------------------------------
/**
 * Os degraus da moeda, do mais valioso para o menos.
 *
 * Derivada do catálogo em vez de escrita à mão porque a alternativa é uma segunda tabela que
 * ninguém lembra de atualizar: uma moeda nova com `value` errado numa lista paralela seria um
 * item que se cunha por mil e se gasta por cem, e o defeito só apareceria no dia em que
 * alguém lucrasse com ele.
 *
 * A ordem é o contrato: quem cunha desce a lista (paga com a maior primeiro), quem gasta a
 * sobe (quebra a menor que resolva). É o que faz o troco ser o menor possível sem nenhuma
 * regra escrita sobre nomes de moeda.
 *
 * Mora no protocolo, e não no servidor, pelo mesmo motivo que o resto do catálogo: o cliente
 * desenha a platina com outra cor, e uma segunda ideia de "o que é moeda" de cada lado do fio
 * seria uma pilha prateada que o servidor conta como ouro.
 */
export const COINS = Object.values(ITEMS)
    .filter((type) => type.kind === ItemKind.Currency)
    .sort((a, b) => b.value - a.value);
/**
 * O tipo desta moeda, ou `undefined` se o item não for dinheiro.
 *
 * Uma função e não duas — "é moeda?" e "quanto vale?" — porque quem pergunta a primeira quer
 * sempre a segunda em seguida, e separá-las duplicaria a busca no catálogo em todo laço que
 * varre um inventário.
 */
export function coinType(typeId) {
    const type = ITEMS[typeId];
    return type?.kind === ItemKind.Currency ? type : undefined;
}
/** Peso de uma pilha, já multiplicado pela quantidade. */
export function stackWeight(typeId, count) {
    const type = ITEMS[typeId];
    return type ? type.weight * count : 0;
}
/** `true` se o item pode ir para aquele slot de equipamento. */
export function fitsSlot(typeId, slot) {
    const type = ITEMS[typeId];
    return type !== undefined && type.slot === slot;
}
/**
 * A peça se gasta com o uso? (PRD §15.7)
 *
 * Função e não comparação escrita à mão porque as duas pontas do desgaste — o golpe que
 * consome e o balcão que conserta — estão em módulos diferentes, e uma cópia em cada
 * discordaria no primeiro item que ganhasse durabilidade.
 */
export function wearsOut(typeId) {
    return (ITEMS[typeId]?.durability ?? 0) > 0;
}
// --- Quem compra o quê (PRD §15.7) ------------------------------------------
//
// `ItemType.value` estava declarado no catálogo inteiro e era lido **zero** vezes: números
// mortos, um por item. Ele vira o preço de venda de fallback — o que o balcão paga por algo
// que não está na vitrine dele.
//
// Mas **dentro da especialidade**, e não em qualquer balcão. Comprador universal
// contradiria §15.8 no mesmo plano: cinco lojas por especialidade só obrigam o jogador a
// andar pela cidade se cada uma comprar coisas diferentes. Um único NPC que compra tudo é
// uma cidade que ninguém memoriza.
/**
 * Fração do `value` que um NPC paga por item fora da vitrine dele.
 *
 * Bem abaixo da margem de uma linha declarada, e é o ponto: a vitrine é a oferta boa, o
 * fallback é o desconto por conveniência. Sem essa diferença, escrever a linha de compra no
 * catálogo não teria consequência nenhuma, e a especialidade viraria decoração.
 */
export const FALLBACK_SELL_RATE = 0.4;
/**
 * Este NPC compra este item fora da vitrine, e por quanto?
 *
 * `0` = não compra. E existe **de propósito** uma classe que nenhum NPC compra: tudo de
 * `Rare` para cima. É a escassez de comprador que cria mercado entre jogadores — sem ela, o
 * canal de Comércio, o trade de §13.6 e o depósito não têm o que fazer, porque todo item tem
 * um balcão que o converte em ouro na hora.
 *
 * O piso continua em `Rare` mesmo depois de §15.11 ter aberto a vitrine até essa raridade, e
 * a assimetria é a decisão: a cidade **vende** peça rara e não a **recompra** avulsa. Quem
 * comprou do Armeiro sabe onde revender — na linha declarada do próprio Armeiro, pela margem
 * de §13.7. O que a peça rara não tem é o comprador universal que a converteria em ouro em
 * qualquer balcão da vila, que é o que apagaria o único motivo de dois jogadores conversarem
 * sobre preço.
 *
 * Note que a regra é sobre o **fallback**: uma loja continua podendo listar um item raro na
 * vitrine e comprá-lo pelo preço declarado. O que ela não faz é comprá-lo por acidente.
 */
export function fallbackSellPrice(typeId, specialty) {
    const type = ITEMS[typeId];
    if (!type || !specialty || type.value <= 0)
        return 0;
    if (type.rarity !== undefined && type.rarity >= ItemRarity.Rare)
        return 0;
    if (!specialty.includes(type.kind))
        return 0;
    // Piso de 1: um item com `value` baixo o bastante para arredondar a zero seria um item
    // que o balcão aceita e não paga, e o jogador teria acabado de doá-lo sem ser avisado.
    return Math.max(1, Math.floor(type.value * FALLBACK_SELL_RATE));
}
//# sourceMappingURL=items.js.map